import { useState, useCallback } from 'react';
import { message } from 'antd';
import api from '../services/api';
import type { SKU, Period, CompetitorASIN, SKUSummary } from '../types';

/**
 * 安全提取数组数据
 * 处理 API 返回的多种格式: 直接数组、{results: [...]}、或其他非数组格式
 */
const extractArrayData = <T>(data: any): T[] => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
};

/**
 * SKU基础数据管理Hook
 * 负责加载和管理SKU、周期、关键词、竞争对手和汇总数据
 */
export const useSKUData = (skuId: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [sku, setSku] = useState<SKU | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [keywords, setKeywords] = useState<any[]>([]);
  const [competitors, setCompetitors] = useState<CompetitorASIN[]>([]);
  const [summary, setSummary] = useState<SKUSummary | null>(null);

  const loadData = useCallback(async () => {
    if (!skuId) return;

    try {
      setLoading(true);
      const [skuData, periodsData, competitorsData, summaryData, keywordsData] = await Promise.all([
        api.get(`/skus/${skuId}/`),
        api.get(`/skus/${skuId}/periods/`),
        api.get(`/skus/${skuId}/competitors/`),
        api.get(`/skus/${skuId}/summary/`),
        api.get(`/skus/${skuId}/aggregated_keywords/`),
      ]);

      console.log('SKU Data loaded:', skuData.data);
      console.log('Periods loaded:', periodsData.data);
      console.log('Keywords loaded:', keywordsData.data);
      console.log('Summary loaded:', summaryData.data);

      setSku(skuData.data);
      setPeriods(extractArrayData<Period>(periodsData.data));
      setCompetitors(extractArrayData<CompetitorASIN>(competitorsData.data));
      setSummary(summaryData.data);
      setKeywords(extractArrayData<any>(keywordsData.data));
    } catch (error) {
      message.error('加载SKU数据失败');
      console.error('Error loading SKU data:', error);
    } finally {
      setLoading(false);
    }
  }, [skuId]);

  const reload = useCallback(() => {
    loadData();
  }, [loadData]);

  return {
    loading,
    sku,
    periods,
    keywords,
    competitors,
    summary,
    loadData,
    reload,
  };
};

